import { Input } from '@ntconsult/aluma-react';
import { Search, X } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectedOption } from '../hooks/useAllocationFilters';
import type { PaginatedListResponse } from '../types/Filters';

type SearchResult<T> = {
  data?: PaginatedListResponse<T>;
  isFetching: boolean;
};

type SearchComboboxProps<T extends { id: number }> = {
  label?: string;
  placeholder: string;
  value: SelectedOption | null;
  onChange: (option: SelectedOption | null, item: T | null) => void;
  useSearch: (text: string) => SearchResult<T>;
  getLabel: (item: T) => string;
  invalid?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Injetados pelo `FormField` via `cloneElement` — precisam chegar no `Input` real, senão o `<label htmlFor>` fica órfão. */
  id?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean | 'true' | 'false';
  required?: boolean;
};

/**
 * composto ad-hoc: combobox de busca assíncrona. O `Combobox` do catálogo
 * filtra só sobre `Combobox.Option`s já montadas em memória (sem
 * `onSearchChange`) — não serve pra buscar entre ~700 profissionais ou ~80
 * projetos no servidor. Composição própria com `Input` + painel flutuante,
 * usando só tokens semânticos do Aluma.
 */
export function SearchCombobox<T extends { id: number }>({
  label,
  placeholder,
  value,
  onChange,
  useSearch,
  getLabel,
  invalid,
  disabled,
  size,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-required': ariaRequired,
  required,
}: SearchComboboxProps<T>) {
  const { t } = useTranslation('alocacao');
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  const { data, isFetching } = useSearch(text);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="relative">
      <Input
        id={id}
        aria-label={label}
        aria-describedby={ariaDescribedBy}
        aria-required={ariaRequired}
        required={required}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        leftIcon={<Search size={16} />}
        rightIcon={
          value && !disabled ? (
            <button
              type="button"
              aria-label={t('filtros.limparSelecao')}
              className="pointer-events-auto flex h-full items-center text-foreground-subtle hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(null, null);
                setText('');
              }}
            >
              <X size={16} />
            </button>
          ) : undefined
        }
        placeholder={placeholder}
        disabled={disabled}
        invalid={invalid}
        size={size}
        value={open ? text : (value?.name ?? '')}
        onFocus={() => {
          setOpen(true);
          setText('');
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-surface p-1 shadow-lg"
        >
          <div className="max-h-60 overflow-y-auto overflow-x-hidden">
            {isFetching && (
              <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
                {t('filtros.buscando')}
              </div>
            )}
            {!isFetching && items.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-foreground-subtle">
                {t('filtros.semResultados')}
              </div>
            )}
            {!isFetching &&
              items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="option"
                  aria-selected={item.id === value?.id}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    const label = getLabel(item);
                    onChange({ id: item.id, name: label }, item);
                    setOpen(false);
                  }}
                >
                  {getLabel(item)}
                </button>
              ))}
          </div>
          {!isFetching && total > items.length && (
            <div className="border-t border-border px-3 pt-2 pb-1 text-xs text-foreground-subtle">
              {t('filtros.mostrandoDeTotal', { shown: items.length, total })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
